import {
  getInt32,
  getObject,
} from "@surya-digital/leo-ts-types";
import { SurveyBreakdown, SurveyBreakdownDTO } from "./surveyBreakdown";

export interface SurveyLinkBreakdownDTO {
  totalLinks: number;
  submittedLink: SurveyBreakdownDTO;
  startedLink: SurveyBreakdownDTO;
  inProgressLink: SurveyBreakdownDTO;
  lastPageLink: SurveyBreakdownDTO;
  notStartedLink: SurveyBreakdownDTO;
  viewedLink: SurveyBreakdownDTO;
}

export class SurveyLinkBreakdown {
  readonly isSurveyLinkBreakdownSurveyLinkBreakdown: boolean = true;

  readonly totalLinks: number;
  readonly submittedLink: SurveyBreakdown;
  readonly startedLink: SurveyBreakdown;
  readonly inProgressLink: SurveyBreakdown;
  readonly lastPageLink: SurveyBreakdown;
  readonly notStartedLink: SurveyBreakdown;
  readonly viewedLink: SurveyBreakdown;

  static readonly constraints = {
    "totalLinks": {
      "minValue": 0,
    },
  }

  constructor(
  totalLinks: number, 
  submittedLink: SurveyBreakdown, 
  startedLink: SurveyBreakdown, 
  inProgressLink: SurveyBreakdown, 
  lastPageLink: SurveyBreakdown, 
  notStartedLink: SurveyBreakdown, 
  viewedLink: SurveyBreakdown
  ) {
    SurveyLinkBreakdown.validateTotalLinks(totalLinks);
    this.totalLinks = totalLinks;
    this.submittedLink = submittedLink;
    this.startedLink = startedLink;
    this.inProgressLink = inProgressLink;
    this.lastPageLink = lastPageLink;
    this.notStartedLink = notStartedLink;
    this.viewedLink = viewedLink;
  }

  static fromDTO(dto: SurveyLinkBreakdownDTO): SurveyLinkBreakdown {
    const totalLinks = getInt32(dto, "totalLinks", InvalidSurveyLinkBreakdownTotalLinksError);
    const submittedLink = SurveyBreakdown.fromDTO(getObject(dto, "submittedLink", InvalidSurveyLinkBreakdownSubmittedLinkError));
    const startedLink = SurveyBreakdown.fromDTO(getObject(dto, "startedLink", InvalidSurveyLinkBreakdownStartedLinkError));
    const inProgressLink = SurveyBreakdown.fromDTO(getObject(dto, "inProgressLink", InvalidSurveyLinkBreakdownInProgressLinkError));
    const lastPageLink = SurveyBreakdown.fromDTO(getObject(dto, "lastPageLink", InvalidSurveyLinkBreakdownLastPageLinkError));
    const notStartedLink = SurveyBreakdown.fromDTO(getObject(dto, "notStartedLink", InvalidSurveyLinkBreakdownNotStartedLinkError));
    const viewedLink = SurveyBreakdown.fromDTO(getObject(dto, "viewedLink", InvalidSurveyLinkBreakdownViewedLinkError));
    return new SurveyLinkBreakdown(
      totalLinks,
      submittedLink,
      startedLink,
      inProgressLink,
      lastPageLink,
      notStartedLink,
      viewedLink
    );
  }

  toDTO(): SurveyLinkBreakdownDTO {
    const dto: SurveyLinkBreakdownDTO = {
        totalLinks: this.totalLinks,
        submittedLink: this.submittedLink.toDTO(),
        startedLink: this.startedLink.toDTO(),
        inProgressLink: this.inProgressLink.toDTO(),
        lastPageLink: this.lastPageLink.toDTO(),
        notStartedLink: this.notStartedLink.toDTO(),
        viewedLink: this.viewedLink.toDTO()
    };
    return dto;
  }
  private static validateTotalLinks = (totalLinks: number): void => {
    if(totalLinks < 0) {
      throw new InvalidSurveyLinkBreakdownTotalLinksMinValueError(
        0,
        `Attribute totalLinks has value ${totalLinks}. Min value is 0.`
      );
    }
  }
  
}

SurveyLinkBreakdown.prototype.toString = function toString(): string {
  return `SurveyLinkBreakdown(totalLinks='${this.totalLinks}',submittedLink='${this.submittedLink}',startedLink='${this.startedLink}',inProgressLink='${this.inProgressLink}',lastPageLink='${this.lastPageLink}',notStartedLink='${this.notStartedLink}',viewedLink='${this.viewedLink}')`;
};

export class InvalidSurveyLinkBreakdownError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownError";
  }
}
export class InvalidSurveyLinkBreakdownTotalLinksError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownTotalLinksError";
  }
}

export class InvalidSurveyLinkBreakdownTotalLinksMinValueError extends InvalidSurveyLinkBreakdownTotalLinksError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownTotalLinksMinValueError";
  }
}

export class InvalidSurveyLinkBreakdownSubmittedLinkError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownSubmittedLinkError";
  }
}

export class InvalidSurveyLinkBreakdownStartedLinkError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownStartedLinkError";
  }
}

export class InvalidSurveyLinkBreakdownInProgressLinkError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownInProgressLinkError";
  }
}

export class InvalidSurveyLinkBreakdownLastPageLinkError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownLastPageLinkError";
  }
}

export class InvalidSurveyLinkBreakdownNotStartedLinkError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownNotStartedLinkError";
  }
}

export class InvalidSurveyLinkBreakdownViewedLinkError extends InvalidSurveyLinkBreakdownError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyLinkBreakdownViewedLinkError";
  }
}
